import React, { useRef, useEffect, useState, useCallback } from 'react';
import './DxfViewer.css';

// Full AutoCAD Color Index (ACI) table — 0-255
// Standard colors 1-9 are hardcoded; 10-249 are generated from 24 hue groups × 15°
const ACI_COLORS = (() => {
  function hsv(h, s, v) {
    const c = v * s;
    const x = c * (1 - Math.abs((h / 60) % 2 - 1));
    const m = v - c;
    let r, g, b;
    if      (h < 60)  { r = c; g = x; b = 0; }
    else if (h < 120) { r = x; g = c; b = 0; }
    else if (h < 180) { r = 0; g = c; b = x; }
    else if (h < 240) { r = 0; g = x; b = c; }
    else if (h < 300) { r = x; g = 0; b = c; }
    else              { r = c; g = 0; b = x; }
    return '#' + [r + m, g + m, b + m]
      .map(n => Math.round(n * 255).toString(16).padStart(2, '0'))
      .join('');
  }
  // 10 shade variants per hue group (saturation, value)
  const S = [1.0, 0.5, 0.25, 0.125, 0.75, 1.0,  0.5,  0.25, 1.0,  0.5 ];
  const V = [1.0, 1.0, 1.0,  1.0,   1.0,  0.65, 0.65, 0.65, 0.50, 0.50];
  const t = {
    0: '#000000',
    1: '#ff0000', 2: '#ffff00', 3: '#00ff00', 4: '#00ffff',
    5: '#0000ff', 6: '#ff00ff', 7: '#ffffff', 8: '#414141', 9: '#808080',
    250: '#333333', 251: '#505050', 252: '#696969',
    253: '#808080', 254: '#b3b3b3', 255: '#e6e6e6',
  };
  for (let g = 0; g < 24; g++) {
    const h = g * 15;
    const base = 10 + g * 10;
    for (let i = 0; i < 10; i++) t[base + i] = hsv(h, S[i], V[i]);
  }
  return t;
})();

// Convert a raw DXF color number to a CSS color string.
// Handles: ACI 0-255, true color 24-bit RGB, negative (layer-off) values.
function colorNumberToCss(n) {
  if (typeof n !== 'number' || !isFinite(n)) return null;
  const abs = Math.abs(n);
  if (abs <= 255) {
    // ACI index (0 = BYBLOCK, 256 = BYLAYER handled elsewhere)
    return ACI_COLORS[abs] || null;
  }
  // 24-bit true color (RGB packed into int): 0x00RRGGBB
  if (abs <= 0xffffff) {
    return '#' + abs.toString(16).padStart(6, '0');
  }
  return null;
}

function resolveColor(entity, layers) {
  // Direct color from serialized Three.js groups
  if (entity._directColor && typeof entity.color === 'string') return entity.color;

  // dxf-parser variants store the entity's own color in either `colorIndex` or
  // `color`. 256 means ByLayer, 0 means ByBlock; any other value is explicit.
  const ownIdx = entity.colorIndex;
  const ownColor = entity.color;
  const isByLayerIdx = ownIdx === 256 || ownIdx === undefined;

  if (!isByLayerIdx && ownIdx !== 0) {
    const c = colorNumberToCss(ownIdx);
    if (c) return c;
  }
  // Explicit `color` field (can be ACI or 24-bit RGB)
  if (ownColor != null && typeof ownColor === 'number' && ownColor !== 256 && ownColor !== 0) {
    const c = colorNumberToCss(ownColor);
    if (c) return c;
  }

  // Fall back to layer color
  const layer = layers?.[entity.layer];
  if (layer) {
    // dxf-parser layer fields: `color` (ACI or RGB), sometimes `colorIndex`
    const lc = layer.color != null ? layer.color : layer.colorIndex;
    const c = colorNumberToCss(lc);
    if (c) return c;
  }

  return '#aaaaaa';
}

function getBBox(entities, blocks, depth = 0) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const expand = (x, y) => {
    if (isFinite(x) && isFinite(y)) {
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
    }
  };
  for (const e of entities) {
    switch (e.type) {
      case 'LINE':
        if (e.vertices) { e.vertices.forEach(v => expand(v.x, v.y)); }
        else { expand(e.start?.x, e.start?.y); expand(e.end?.x, e.end?.y); }
        break;
      case 'CIRCLE': case 'ARC':
        expand(e.center?.x - (e.radius || 0), e.center?.y - (e.radius || 0));
        expand(e.center?.x + (e.radius || 0), e.center?.y + (e.radius || 0));
        break;
      case 'LWPOLYLINE': case 'POLYLINE':
        (e.vertices || []).forEach(v => expand(v.x, v.y));
        break;
      case 'ELLIPSE':
        if (e.center) {
          const mx = Math.abs(e.majorAxisEndPoint?.x || 0);
          const my = Math.abs(e.majorAxisEndPoint?.y || 0);
          const r = Math.max(mx, my) * 1.1;
          expand(e.center.x - r, e.center.y - r);
          expand(e.center.x + r, e.center.y + r);
        }
        break;
      case 'SPLINE':
        (e.controlPoints || e.fitPoints || []).forEach(v => expand(v.x, v.y));
        break;
      case 'TEXT': case 'MTEXT':
        expand(e.startPoint?.x || e.position?.x, e.startPoint?.y || e.position?.y);
        break;
      case 'INSERT': {
        if (!blocks || !e.name || depth > 8) {
          expand(e.position?.x, e.position?.y);
          break;
        }
        const block = blocks[e.name];
        if (!block?.entities?.length) {
          expand(e.position?.x, e.position?.y);
          break;
        }
        // Recursively get block's bounding box
        const bb = getBBox(block.entities, blocks, depth + 1);
        if (!isFinite(bb.minX)) { expand(e.position?.x, e.position?.y); break; }
        const ix = e.position?.x || 0;
        const iy = e.position?.y || 0;
        const sx = e.xScale ?? e.scaleX ?? 1;
        const sy = e.yScale ?? e.scaleY ?? 1;
        const rot = (e.rotation || 0) * Math.PI / 180;
        // Transform the 4 corners of the block bbox
        const corners = [
          { x: bb.minX, y: bb.minY }, { x: bb.maxX, y: bb.minY },
          { x: bb.maxX, y: bb.maxY }, { x: bb.minX, y: bb.maxY },
        ];
        const cosR = Math.cos(rot), sinR = Math.sin(rot);
        for (const c of corners) {
          const lx = c.x * sx, ly = c.y * sy;
          expand(ix + lx * cosR - ly * sinR, iy + lx * sinR + ly * cosR);
        }
        break;
      }
      default: break;
    }
  }
  if (!isFinite(minX)) return { minX: 0, minY: 0, maxX: 100, maxY: 100 };
  return { minX, minY, maxX, maxY };
}

function drawEntities(ctx, entities, layers, blocks, scale, offsetX, offsetY) {
  ctx.lineWidth = 1 / scale;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  for (const e of entities) {
    const color = resolveColor(e, layers);
    ctx.strokeStyle = color;
    ctx.fillStyle = color;

    switch (e.type) {
      case 'LINE': {
        const sx = e.vertices ? e.vertices[0]?.x : e.start?.x;
        const sy = e.vertices ? e.vertices[0]?.y : e.start?.y;
        const ex = e.vertices ? e.vertices[1]?.x : e.end?.x;
        const ey = e.vertices ? e.vertices[1]?.y : e.end?.y;
        if (sx == null || ex == null) break;
        ctx.beginPath();
        ctx.moveTo(sx, -sy);
        ctx.lineTo(ex, -ey);
        ctx.stroke();
        break;
      }
      case 'CIRCLE': {
        if (!e.center || !e.radius || e.radius <= 0) break;
        ctx.beginPath();
        ctx.arc(e.center.x, -e.center.y, Math.abs(e.radius), 0, Math.PI * 2);
        ctx.stroke();
        break;
      }
      case 'ARC': {
        if (!e.center || !e.radius) break;
        const arcR = Math.abs(e.radius);
        if (arcR <= 0) break;
        const sa = -(e.endAngle || 0) * Math.PI / 180;
        const ea = -(e.startAngle || 0) * Math.PI / 180;
        ctx.beginPath();
        ctx.arc(e.center.x, -e.center.y, arcR, sa, ea);
        ctx.stroke();
        break;
      }
      case 'ELLIPSE': {
        if (!e.center || !e.majorAxisEndPoint) break;
        const mx = e.majorAxisEndPoint.x || 0;
        const my = e.majorAxisEndPoint.y || 0;
        const major = Math.sqrt(mx * mx + my * my);
        const minor = major * (e.axisRatio || 1);
        const rot = Math.atan2(my, mx);
        ctx.beginPath();
        ctx.ellipse(e.center.x, -e.center.y, major, minor, -rot, 0, Math.PI * 2);
        ctx.stroke();
        break;
      }
      case 'LWPOLYLINE': case 'POLYLINE': {
        const verts = e.vertices || [];
        if (verts.length < 2) break;
        ctx.beginPath();
        ctx.moveTo(verts[0].x, -verts[0].y);
        for (let i = 1; i < verts.length; i++) {
          ctx.lineTo(verts[i].x, -verts[i].y);
        }
        if (e.shape || e.closed) ctx.closePath();
        ctx.stroke();
        break;
      }
      case 'SPLINE': {
        const pts = e.fitPoints || e.controlPoints || [];
        if (pts.length < 2) break;
        ctx.beginPath();
        ctx.moveTo(pts[0].x, -pts[0].y);
        if (pts.length === 2) {
          ctx.lineTo(pts[1].x, -pts[1].y);
        } else {
          for (let i = 1; i < pts.length - 1; i++) {
            const xc = (pts[i].x + pts[i + 1].x) / 2;
            const yc = (pts[i].y + pts[i + 1].y) / 2;
            ctx.quadraticCurveTo(pts[i].x, -pts[i].y, xc, -yc);
          }
          const last = pts[pts.length - 1];
          ctx.lineTo(last.x, -last.y);
        }
        ctx.stroke();
        break;
      }
      case 'TEXT': case 'MTEXT': {
        const px = e.startPoint?.x || e.position?.x || 0;
        const py = e.startPoint?.y || e.position?.y || 0;
        const h = e.textHeight || e.height || 2;
        const text = (e.text || '').replace(/\\P/g, '\n').replace(/\\[^;]*;/g, '').replace(/\{|\}/g, '');
        if (!text.trim()) break;
        ctx.save();
        ctx.translate(px, -py);
        const fontSize = Math.max(h, 0.5);
        ctx.font = `${fontSize}px sans-serif`;
        ctx.fillText(text, 0, 0);
        ctx.restore();
        break;
      }
      case 'INSERT': {
        if (!blocks || !e.name) break;
        const block = blocks[e.name];
        if (!block || !block.entities) break;
        ctx.save();
        ctx.translate(e.position?.x || 0, -(e.position?.y || 0));
        if (e.rotation) ctx.rotate(-e.rotation * Math.PI / 180);
        const sx = e.xScale ?? e.scaleX ?? 1;
        const sy = e.yScale ?? e.scaleY ?? 1;
        ctx.scale(sx, sy);
        // Subtract block base point so entities appear at correct position
        const bpx = block.position?.x || 0;
        const bpy = block.position?.y || 0;
        if (bpx !== 0 || bpy !== 0) ctx.translate(-bpx, bpy);
        drawEntities(ctx, block.entities, layers, blocks, scale * Math.max(Math.abs(sx), Math.abs(sy)), 0, 0);
        ctx.restore();
        break;
      }
      default: break;
    }
  }
}

function collectTextEntities(entities, blocks, offsetX = 0, offsetY = 0) {
  const result = [];
  for (const e of entities) {
    if (e.type === 'TEXT' || e.type === 'MTEXT') {
      const px = (e.startPoint?.x || e.position?.x || 0) + offsetX;
      const py = (e.startPoint?.y || e.position?.y || 0) + offsetY;
      const h = e.textHeight || e.height || 2;
      const text = (e.text || '').replace(/\\P/g, '\n').replace(/\\[^;]*;/g, '').replace(/\{|\}/g, '').trim();
      if (text) result.push({ text, x: px, y: py, height: h });
    } else if (e.type === 'INSERT' && blocks && e.name) {
      const block = blocks[e.name];
      if (block?.entities) {
        const ix = (e.position?.x || 0) + offsetX;
        const iy = (e.position?.y || 0) + offsetY;
        result.push(...collectTextEntities(block.entities, blocks, ix, iy));
      }
    }
  }
  return result;
}

const DxfViewer = ({ dxfData, className = '', height = 300, pickMode = false, onTextPick }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [viewState, setViewState] = useState({ scale: 1, offsetX: 0, offsetY: 0 });
  const dragRef = useRef({ dragging: false, lastX: 0, lastY: 0, startX: 0, startY: 0 });
  const viewStateRef = useRef(viewState);
  viewStateRef.current = viewState;
  const initialFitDone = useRef(false);

  const entities = dxfData?.entities || [];
  const blocks = dxfData?.blocks || {};
  const layerTable = dxfData?.tables?.layer?.layers || {};
  const layers = {};
  if (Array.isArray(layerTable)) {
    layerTable.forEach(l => { layers[l.name] = l; });
  } else if (typeof layerTable === 'object') {
    Object.assign(layers, layerTable);
  }

  // Size canvas and fit drawing
  const sizeAndFit = useCallback(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas || !entities.length) return;

    const rect = container.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);

    const bbox = getBBox(entities, blocks);
    const dw = bbox.maxX - bbox.minX || 1;
    const dh = bbox.maxY - bbox.minY || 1;
    const fillRatio = 0.5;
    const s = Math.min((rect.width * fillRatio) / dw, (rect.height * fillRatio) / dh);
    const ox = (rect.width / 2) - ((bbox.minX + bbox.maxX) / 2) * s;
    const oy = (rect.height / 2) + ((bbox.minY + bbox.maxY) / 2) * s;

    setViewState({ scale: s * dpr, offsetX: ox * dpr, offsetY: oy * dpr });
    initialFitDone.current = true;
  }, [entities, blocks]);

  // Init on mount and when entities change
  useEffect(() => {
    if (!entities.length) return;
    // Small delay to ensure container is laid out
    const t = setTimeout(sizeAndFit, 50);
    return () => clearTimeout(t);
  }, [entities, sizeAndFit]);

  // Render
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !entities.length) return;
    const ctx = canvas.getContext('2d');
    const { scale, offsetX, offsetY } = viewState;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.translate(offsetX, offsetY);
    ctx.scale(scale, scale);

    drawEntities(ctx, entities, layers, blocks, scale, offsetX, offsetY);

    ctx.restore();
  }, [entities, viewState, layers, blocks]);

  // Mouse pan
  const handleMouseDown = useCallback((e) => {
    dragRef.current = { dragging: true, lastX: e.clientX, lastY: e.clientY, startX: e.clientX, startY: e.clientY };
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!dragRef.current.dragging) return;
    const dpr = window.devicePixelRatio || 1;
    const dx = (e.clientX - dragRef.current.lastX) * dpr;
    const dy = (e.clientY - dragRef.current.lastY) * dpr;
    dragRef.current.lastX = e.clientX;
    dragRef.current.lastY = e.clientY;
    setViewState(v => ({ ...v, offsetX: v.offsetX + dx, offsetY: v.offsetY + dy }));
  }, []);

  const handleMouseUp = useCallback(() => {
    dragRef.current.dragging = false;
  }, []);

  // Text pick on click (not drag)
  const handleCanvasClick = useCallback((e) => {
    if (!pickMode || !onTextPick) return;
    const { startX, startY } = dragRef.current;
    if (startX == null) return;
    const dist = Math.sqrt((e.clientX - startX) ** 2 + (e.clientY - startY) ** 2);
    if (dist > 5) return; // was a drag, not a click
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const cx = (e.clientX - rect.left) * dpr;
    const cy = (e.clientY - rect.top) * dpr;
    const vs = viewStateRef.current;
    const drawX = (cx - vs.offsetX) / vs.scale;
    const drawY = -((cy - vs.offsetY) / vs.scale);
    const textEntities = collectTextEntities(entities, blocks);
    let nearest = null;
    let minDist = Infinity;
    for (const t of textEntities) {
      const d = Math.sqrt((t.x - drawX) ** 2 + (t.y - drawY) ** 2);
      if (d < minDist) { minDist = d; nearest = t; }
    }
    if (nearest && minDist < Math.max(nearest.height * 5, 50 / vs.scale)) {
      onTextPick(nearest.text);
    }
  }, [pickMode, onTextPick, entities, blocks]);

  // Zoom - native listener to allow preventDefault on non-passive
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const handler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      const mx = (e.clientX - rect.left) * dpr;
      const my = (e.clientY - rect.top) * dpr;
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      setViewState(v => ({
        scale: v.scale * factor,
        offsetX: mx - (mx - v.offsetX) * factor,
        offsetY: my - (my - v.offsetY) * factor
      }));
    };
    canvas.addEventListener('wheel', handler, { passive: false });
    return () => canvas.removeEventListener('wheel', handler);
  }, []);

  // Touch pan/zoom
  const touchRef = useRef({ touches: [], lastDist: 0 });

  const handleTouchStart = useCallback((e) => {
    const touches = Array.from(e.touches);
    touchRef.current.touches = touches.map(t => ({ x: t.clientX, y: t.clientY }));
    if (touches.length === 2) {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      touchRef.current.lastDist = Math.sqrt(dx * dx + dy * dy);
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    e.preventDefault();
    const touches = Array.from(e.touches);
    const prev = touchRef.current.touches;

    if (touches.length === 1 && prev.length >= 1) {
      const dx = touches[0].clientX - prev[0].x;
      const dy = touches[0].clientY - prev[0].y;
      setViewState(v => ({ ...v, offsetX: v.offsetX + dx, offsetY: v.offsetY + dy }));
    } else if (touches.length === 2 && prev.length >= 2) {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const factor = dist / (touchRef.current.lastDist || dist);
      const cx = (touches[0].clientX + touches[1].clientX) / 2;
      const cy = (touches[0].clientY + touches[1].clientY) / 2;
      const canvas = canvasRef.current;
      const rect = canvas?.getBoundingClientRect();
      const mx = cx - (rect?.left || 0);
      const my = cy - (rect?.top || 0);

      setViewState(v => ({
        scale: v.scale * factor,
        offsetX: mx - (mx - v.offsetX) * factor,
        offsetY: my - (my - v.offsetY) * factor
      }));
      touchRef.current.lastDist = dist;
    }

    touchRef.current.touches = touches.map(t => ({ x: t.clientX, y: t.clientY }));
  }, []);

  // Reset view
  const handleReset = useCallback(() => {
    initialFitDone.current = false;
    sizeAndFit();
  }, [sizeAndFit]);

  if (!entities.length) {
    return (
      <div className={`dxf-viewer dxf-viewer-empty ${className}`} style={{ height }}>
        <span>DXF verisi bulunamadi</span>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`dxf-viewer ${pickMode ? 'pick-mode' : ''} ${className}`}
      style={{ height }}
    >
      <canvas
        ref={canvasRef}
        className="dxf-viewer-canvas"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={handleCanvasClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={() => { touchRef.current.touches = []; }}
      />
      <div className="dxf-viewer-controls">
        <button onClick={() => setViewState(v => ({ ...v, scale: v.scale * 1.3, offsetX: v.offsetX, offsetY: v.offsetY }))} title="Yakinlastir">+</button>
        <button onClick={() => setViewState(v => ({ ...v, scale: v.scale / 1.3, offsetX: v.offsetX, offsetY: v.offsetY }))} title="Uzaklastir">−</button>
        <button onClick={handleReset} title="Sifirla">⟳</button>
      </div>
      <div className="dxf-viewer-hint">Kaydirmak icin surukle, zum icin scroll</div>
    </div>
  );
};

export default DxfViewer;
